import { chunkArray } from '@vivino/js-web-common';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { track } from 'vivino-js/analytics';
import { fetchWineReviewsByTaste } from 'vivino-js/api/reviews';
import useDebounce from 'vivino-js/hooks/useDebounce';

import TasteReviewsPresentational from './TasteReviews.presentational';

const KEYWORDS_PER_COLUMN = 2;

const REVIEWS_PER_PAGE = 10;

const TasteReviews = ({ show, onClose, note, wineId, vintageYear }) => {
  const [shownKeywordPairs, setShownKeywordPairs] = useState([]);
  const [selectedKeywordIds, setSelectedKeywordIds] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [isFetchingReviews, setIsFetchingReviews] = useState(true);
  const [allReviewsLoaded, setAllReviewsLoaded] = useState(false);
  const [debouncedKeywordIds, isDebouncing] = useDebounce({
    value: selectedKeywordIds,
    delay: 800,
  });
  const [scrollToTopUpdater, setScrollToTopUpdater] = useState(0);
  const [page, setPage] = useState();

  useEffect(() => {
    if (note?.primary_keywords) {
      setShownKeywordPairs(chunkArray(note.primary_keywords, KEYWORDS_PER_COLUMN));
    }
  }, [note]);

  useEffect(() => {
    // When selected notes change reset the reviews
    setScrollToTopUpdater(scrollToTopUpdater + 1);
    if (show) {
      track({
        event: 'Taste characteristics - filter taste notes',
        props: {
          noteGroup: note.group,
          tasteIds: debouncedKeywordIds,
        },
      });

      setReviews([]);
      setPage(1);
      setAllReviewsLoaded(false);
      const flavorIds =
        debouncedKeywordIds.length > 0
          ? debouncedKeywordIds
          : note.primary_keywords.map(({ id }) => id.toString());

      fetchAndSetReviews({
        page: 1,
        reviews: [],
        flavorIds,
      });
    }
  }, [show, debouncedKeywordIds.length]);

  const fetchAndSetReviews = ({ page, reviews, flavorIds }) => {
    setIsFetchingReviews(true);
    fetchWineReviewsByTaste({
      wineId,
      flavorIds,
      page,
      perPage: REVIEWS_PER_PAGE,
    })
      .then((data) => {
        setReviews([...reviews, ...data.reviews]);
        if (data.reviews.length < 10) {
          setAllReviewsLoaded(true);
        }
      })
      .finally(() => {
        setIsFetchingReviews(false);
      });
  };

  const toggleKeyword = (e) => {
    const id = e.currentTarget.id;
    if (selectedKeywordIds.includes(id)) {
      // deselect this keyword
      setSelectedKeywordIds(selectedKeywordIds.filter((keywordId) => id !== keywordId));
    } else {
      // select this keyword
      setSelectedKeywordIds([...selectedKeywordIds, id]);
    }
    setAllReviewsLoaded(false);
  };

  const handleIntersect = () => {
    if (!isFetchingReviews && !isDebouncing && !allReviewsLoaded) {
      const newPage = page + 1;
      track({
        event: 'Taste characteristics - lightbox page view',
        props: {
          noteGroup: note.group,
          page: newPage,
        },
      });

      setPage(newPage);
      fetchAndSetReviews({
        page: newPage,
        reviews,
        flavorIds: debouncedKeywordIds,
      });
    }
  };

  const handleReviewDeleted = ({ reviewId }) => {
    const filteredReviews = reviews.filter((review) => review.id !== reviewId);
    setReviews(filteredReviews);
  };

  return (
    <TasteReviewsPresentational
      onClose={onClose}
      note={note}
      show={show}
      onIntersect={handleIntersect}
      isLoading={isFetchingReviews || isDebouncing}
      onNoteTagClick={toggleKeyword}
      selectedKeywordIds={selectedKeywordIds}
      shownKeywordPairs={shownKeywordPairs}
      reviews={reviews}
      onReviewDeleted={handleReviewDeleted}
      scrollToTopUpdater={scrollToTopUpdater}
      vintageYear={vintageYear}
    />
  );
};

TasteReviews.propTypes = {
  show: PropTypes.bool.isRequired,
  note: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  wineId: PropTypes.number.isRequired,
  vintageYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default TasteReviews;
